import {
  getRelaxBaseCode,
  isRelaxIntegratedUnitByDefaults,
  parseRelaxArmVariantCatalog,
} from '@utils/atemporal/relaxArmVariants'
import { getBudgetDimensionWordLabels, ui } from '@utils/i18n/uiCopy'

export type PieceDimensions = {
  width: number
  thickness: number
  height: number
}

/** Convierte dimensiones de item (mm internos, ×10) a cm. */
export function itemDimensionToCm(value: unknown): number | null {
  if (typeof value !== 'number' || Number.isNaN(value)) return null
  return value / 10
}

/** Muestra cm sin decimales si es entero; si no, un decimal (ej. 20.1). */
export function formatItemDimensionCm(value: unknown): string {
  const cm = itemDimensionToCm(value)
  if (cm == null) return '-'
  const rounded = Math.round(cm * 10) / 10
  return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1)
}

/** Formato presupuesto: largo (ancho), fondo y alto en cm. */
export function formatBudgetPieceDimensions({
  width,
  thickness,
  height,
}: PieceDimensions): string {
  const labels = getBudgetDimensionWordLabels()
  const largoCm = formatItemDimensionCm(width)
  const fondoCm = formatItemDimensionCm(thickness)
  const altoCm = formatItemDimensionCm(height)
  return `(${labels.largo} ${largoCm}cm, ${labels.fondo}: ${fondoCm}cm ${ui('and')} ${labels.alto}: ${altoCm}cm)`
}

/** Convierte valor del menú (mm internos) a cm para mostrar. */
export function menuDimensionToCm(value: unknown): number | null {
  return itemDimensionToCm(value)
}

export function formatMenuDimensionCm(value: unknown): string {
  const formatted = formatItemDimensionCm(value)
  return formatted === '-' ? '-' : `${formatted}cm`
}

/**
 * Referencia visible:
 * - Relax «167 BR3 I» → «167» (sin BR ni lado)
 * - resto: tramo antes del primer guion («160-142» → «160»)
 */
export function formatCatalogBaseReference(value: unknown, fallback = '—'): string {
  const raw = String(value ?? '').trim()
  if (!raw || raw === '-') return fallback

  const relaxVariant = parseRelaxArmVariantCatalog(raw)
  if (relaxVariant?.baseCode) return relaxVariant.baseCode

  if (isRelaxIntegratedUnitByDefaults(raw)) {
    return getRelaxBaseCode(raw) || fallback
  }

  const beforeHyphen = raw.split('-')[0]?.trim() || ''
  // Por si llega «167 BR3 I» sin pasar los detectores anteriores.
  const withoutBrTail = beforeHyphen.replace(/\s+BR\d+\b.*$/i, '').trim()
  return withoutBrTail || beforeHyphen || fallback
}

